import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    ShieldCheck, MessageSquare, Users, Loader2, CheckCircle2, XCircle, Send,
    Search, Trash2, UserPlus, Filter, Star, Clock, RotateCcw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    pending_approval: { label: "Pending", tone: "bg-yellow-300/20 text-yellow-300 border-yellow-300/40" },
    revisions_needed: { label: "Revisions", tone: "bg-orange-400/20 text-orange-300 border-orange-400/40" },
};

const Approvals = () => {
    const { token, user } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [status, setStatus] = useState("pending_approval");
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [recipientSearch, setRecipientSearch] = useState("");
    const [addBlob, setAddBlob] = useState("");
    const [rejectReason, setRejectReason] = useState("");
    const [approveNotes, setApproveNotes] = useState("");
    const [approveDispatch, setApproveDispatch] = useState(true);
    const [dispatchBusy, setDispatchBusy] = useState(false);

    const loadQueue = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/approvals/queue`, { headers: authH, params: { status } });
            setQueue(r.data?.queue || []);
        } catch (e) {
            toast.error("Queue load failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [status, authH]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    const openDetail = async (campaignId) => {
        setDetailBusy(true);
        try {
            const r = await axios.get(`${API}/admin/approvals/${campaignId}`, { headers: authH });
            setDetail(r.data);
            setSelected(new Set());
            setRecipientSearch("");
            setAddBlob("");
            setRejectReason("");
            setApproveNotes("");
        } catch (e) {
            toast.error("Detail load failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setDetailBusy(false);
        }
    };

    const editRecipients = async (action, payload) => {
        if (!detail) return;
        try {
            await axios.put(
                `${API}/admin/approvals/${detail.campaign.id}/recipients`,
                { action, ...payload },
                { headers: authH },
            );
            await openDetail(detail.campaign.id);   // refresh
            toast.success(`Recipients ${action}ed`);
        } catch (e) {
            toast.error("Edit failed", { description: e?.response?.data?.detail || e.message });
        }
    };

    const removeSelected = async () => {
        if (selected.size === 0) {
            toast.error("Select at least one phone");
            return;
        }
        await editRecipients("remove", { phones: Array.from(selected) });
        setSelected(new Set());
    };

    const addBulk = async () => {
        if (!addBlob.trim()) {
            toast.error("Enter one or more phone numbers");
            return;
        }
        await editRecipients("add", { phones_blob: addBlob });
        setAddBlob("");
    };

    const clearOverride = async () => {
        if (!detail?.has_override) return;
        await editRecipients("clear-override", {});
    };

    const approve = async () => {
        if (!detail) return;
        setDispatchBusy(true);
        try {
            const r = await axios.post(
                `${API}/admin/approvals/${detail.campaign.id}/approve`,
                { notes: approveNotes || null, dispatch_now: approveDispatch },
                { headers: authH, timeout: 60000 },
            );
            if (r.data?.dispatched) {
                toast.success(`Dispatched to ${r.data.dispatched.recipients} phones`);
            } else {
                toast.success("Approved");
            }
            setDetail(null);
            await loadQueue();
        } catch (e) {
            toast.error("Approval failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setDispatchBusy(false);
        }
    };

    const reject = async () => {
        if (!detail || !rejectReason.trim() || rejectReason.trim().length < 3) {
            toast.error("Provide at least 3 chars of feedback for the content manager");
            return;
        }
        try {
            await axios.post(
                `${API}/admin/approvals/${detail.campaign.id}/reject`,
                { reason: rejectReason },
                { headers: authH },
            );
            toast.success("Sent back for revisions");
            setDetail(null);
            await loadQueue();
        } catch (e) {
            toast.error("Reject failed", { description: e?.response?.data?.detail || e.message });
        }
    };

    const filteredRecipients = (detail?.recipients || []).filter((r) => {
        if (!recipientSearch) return true;
        const q = recipientSearch.toLowerCase();
        return (
            r.phone_number.toLowerCase().includes(q) ||
            (r.region || "").toLowerCase().includes(q) ||
            r.teams.some((t) => t.toLowerCase().includes(q))
        );
    });
    const allSelected = filteredRecipients.length > 0 && filteredRecipients.every((r) => selected.has(r.phone_number));

    return (
        <AdminLayout title="Approvals">
            <div className="space-y-6" data-testid="approvals-page">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white flex items-center gap-3">
                            <ShieldCheck className="h-7 w-7 text-electric" />
                            Approval Queue
                        </h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            Content Manager submissions land here for supervisor review — edit the recipient list, then approve to dispatch immediately (or bounce back for revisions).
                        </p>
                    </div>
                    <Tabs value={status} onValueChange={setStatus}>
                        <TabsList className="bg-paper">
                            <TabsTrigger value="pending_approval" data-testid="tab-pending">
                                Pending
                            </TabsTrigger>
                            <TabsTrigger value="revisions_needed" data-testid="tab-revisions">
                                Revisions Needed
                            </TabsTrigger>
                            <TabsTrigger value="all" data-testid="tab-all">All Open</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-white/40">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading queue…
                    </div>
                ) : queue.length === 0 ? (
                    <Card className="bg-paper border-border p-16 text-center">
                        <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-emerald-400" />
                        <div className="text-white/60 font-heading">Inbox zero — no campaigns awaiting approval</div>
                    </Card>
                ) : (
                    <div className="space-y-3" data-testid="approval-queue">
                        {queue.map((c) => {
                            const st = STATUS_META[c.status] || STATUS_META.pending_approval;
                            return (
                                <Card
                                    key={c.id}
                                    className="bg-paper border-border p-4 hover:border-electric/40 cursor-pointer transition-colors"
                                    onClick={() => openDetail(c.id)}
                                    data-testid={`queue-row-${c.id}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                <Badge className={`${st.tone} border text-[10px] uppercase tracking-wider`}>{st.label}</Badge>
                                                <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                    {c.channel?.toUpperCase()}
                                                </Badge>
                                                {c.has_override && (
                                                    <Badge className="bg-electric/20 text-electric border-electric/40 border text-[10px]">
                                                        List edited
                                                    </Badge>
                                                )}
                                                {c.source_item?.kind && (
                                                    <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                        From {c.source_item.kind}
                                                    </Badge>
                                                )}
                                            </div>
                                            <h3 className="font-heading font-bold text-white text-base truncate">{c.name || "(unnamed campaign)"}</h3>
                                            <p className="text-white/70 text-sm mt-1 line-clamp-2 font-mono">{c.content}</p>
                                            <div className="flex items-center gap-4 mt-2 text-[11px] text-white/40 flex-wrap">
                                                <span><MessageSquare className="h-3 w-3 inline mr-1" /> {c.character_count} chars</span>
                                                <span><Users className="h-3 w-3 inline mr-1" /> {c.recipient_count} recipients</span>
                                                {c.created_by?.email && (
                                                    <span>by {c.created_by.email}</span>
                                                )}
                                                <span><Clock className="h-3 w-3 inline mr-1" /> {new Date(c.created_at).toLocaleString()}</span>
                                                {c.rejection_reason && (
                                                    <span className="text-orange-300"><AlertTriangle className="h-3 w-3 inline mr-1" /> {c.rejection_reason}</span>
                                                )}
                                            </div>
                                        </div>
                                        <Button size="sm" className="bg-electric text-white hover:bg-electric/90" data-testid={`queue-review-${c.id}`}>
                                            Review
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Detail dialog */}
                <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                    <DialogContent className="bg-paper border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="approval-detail">
                        {detailBusy || !detail ? (
                            <div className="py-10 text-center text-white/40"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
                        ) : (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="text-white flex items-center gap-2">
                                        <ShieldCheck className="h-5 w-5 text-electric" />
                                        Review · {detail.campaign.name}
                                    </DialogTitle>
                                    <DialogDescription className="text-xs text-white/50">
                                        Edit the recipient list, then Approve to dispatch immediately or Reject to send back for revisions.
                                    </DialogDescription>
                                </DialogHeader>

                                {/* SMS preview */}
                                <Card className="bg-obsidian border-electric/40 p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge className="bg-electric/20 text-electric border-electric/40 border text-[10px] uppercase">SMS Preview</Badge>
                                        <span className="text-[11px] text-white/40">{(detail.campaign.content || "").length} / 160 chars</span>
                                    </div>
                                    <p className="text-sm text-white whitespace-pre-wrap font-mono" data-testid="approval-sms-preview">
                                        {detail.campaign.content}
                                    </p>
                                    {detail.customer_base && (
                                        <div className="mt-2 text-[11px] text-white/50">
                                            Base: <span className="text-white">{detail.customer_base.name}</span>
                                            {detail.customer_base.default_region && <> · region <span className="text-white">{detail.customer_base.default_region}</span></>}
                                        </div>
                                    )}
                                </Card>

                                {/* Recipient list editor */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <Users className="h-4 w-4 text-white/60" />
                                            <span className="font-heading text-sm text-white">Recipients · {detail.recipient_count}</span>
                                            {detail.has_override && (
                                                <Badge className="bg-electric/20 text-electric border-electric/40 border text-[10px]">Edited</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {detail.has_override && (
                                                <Button size="sm" variant="outline" onClick={clearOverride} className="border-border text-white/70" data-testid="clear-override">
                                                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to base
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={removeSelected}
                                                disabled={selected.size === 0}
                                                className="border-orange-400/40 text-orange-300 hover:bg-orange-400/10"
                                                data-testid="remove-selected"
                                            >
                                                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove ({selected.size})
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Search */}
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                                        <Input
                                            value={recipientSearch}
                                            onChange={(e) => setRecipientSearch(e.target.value)}
                                            placeholder="Search phone, team, or region…"
                                            className="pl-10 bg-obsidian border-border text-white"
                                            data-testid="recipient-search"
                                        />
                                    </div>

                                    {/* Recipient table */}
                                    <div className="border border-border rounded-md overflow-hidden max-h-72 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-obsidian sticky top-0 z-10">
                                                <tr>
                                                    <th className="w-10 p-2">
                                                        <Checkbox
                                                            checked={allSelected}
                                                            onCheckedChange={(v) => {
                                                                if (v) {
                                                                    setSelected(new Set([...selected, ...filteredRecipients.map((r) => r.phone_number)]));
                                                                } else {
                                                                    const next = new Set(selected);
                                                                    filteredRecipients.forEach((r) => next.delete(r.phone_number));
                                                                    setSelected(next);
                                                                }
                                                            }}
                                                            data-testid="select-all-recipients"
                                                        />
                                                    </th>
                                                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-white/50 font-heading">Phone</th>
                                                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-white/50 font-heading">Region</th>
                                                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-white/50 font-heading">Teams</th>
                                                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-white/50 font-heading">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRecipients.map((r) => (
                                                    <tr key={r.phone_number} className="border-t border-border/40 hover:bg-white/[0.02]">
                                                        <td className="p-2">
                                                            <Checkbox
                                                                checked={selected.has(r.phone_number)}
                                                                onCheckedChange={(v) => {
                                                                    const next = new Set(selected);
                                                                    if (v) next.add(r.phone_number); else next.delete(r.phone_number);
                                                                    setSelected(next);
                                                                }}
                                                                data-testid={`select-${r.phone_number}`}
                                                            />
                                                        </td>
                                                        <td className="p-2 text-white font-mono text-xs">{r.phone_number}</td>
                                                        <td className="p-2 text-white/70">{r.region || <span className="text-white/30">—</span>}</td>
                                                        <td className="p-2 text-white/70 text-xs">{r.teams.length ? r.teams.join(", ") : <span className="text-white/30">—</span>}</td>
                                                        <td className="p-2">
                                                            {r.active ? (
                                                                <Badge className="bg-emerald-400/20 text-emerald-300 border-emerald-400/40 border text-[10px]">Active</Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="text-[10px] border-white/15 text-white/50">Unsubscribed</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {filteredRecipients.length === 0 && (
                                                    <tr><td colSpan={5} className="p-6 text-center text-white/40 text-sm">No recipients match</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Add bulk */}
                                    <div className="space-y-2 pt-2 border-t border-border">
                                        <div className="text-[11px] uppercase tracking-wider text-white/50 flex items-center gap-1.5">
                                            <UserPlus className="h-3 w-3" /> Add phone numbers (comma / newline / space separated — one or many)
                                        </div>
                                        <Textarea
                                            value={addBlob}
                                            onChange={(e) => setAddBlob(e.target.value)}
                                            placeholder="+255712000001, +255712000002&#10;0712000003"
                                            className="bg-obsidian border-border text-white font-mono text-sm"
                                            rows={3}
                                            data-testid="add-phones-blob"
                                        />
                                        <Button size="sm" onClick={addBulk} className="bg-emerald-400 text-obsidian hover:bg-emerald-300" data-testid="add-phones-go">
                                            <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add to list
                                        </Button>
                                    </div>
                                </div>

                                {/* Decision */}
                                <div className="border-t border-border pt-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[11px] uppercase tracking-wider text-white/50">Approve · notes (optional)</label>
                                            <Textarea value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} rows={2} className="bg-obsidian border-border text-white" data-testid="approve-notes" />
                                            <label className="flex items-center gap-2 text-xs text-white/70 mt-2 cursor-pointer">
                                                <Checkbox checked={approveDispatch} onCheckedChange={setApproveDispatch} data-testid="approve-dispatch" />
                                                <Send className="h-3 w-3" /> Dispatch immediately
                                            </label>
                                        </div>
                                        <div>
                                            <label className="text-[11px] uppercase tracking-wider text-white/50">Reject · reason (required)</label>
                                            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} placeholder="What needs to change?" className="bg-obsidian border-border text-white" data-testid="reject-reason" />
                                        </div>
                                    </div>
                                </div>

                                <DialogFooter className="gap-2">
                                    <Button variant="outline" onClick={reject} className="border-orange-400/40 text-orange-300 hover:bg-orange-400/10" data-testid="reject-btn">
                                        <XCircle className="h-4 w-4 mr-2" /> Send back
                                    </Button>
                                    <Button
                                        disabled={dispatchBusy || detail.recipient_count === 0}
                                        onClick={approve}
                                        className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold"
                                        data-testid="approve-btn"
                                    >
                                        {dispatchBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                                        {approveDispatch ? `Approve & Send (${detail.recipient_count})` : "Approve"}
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default Approvals;
